import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, fmtMoney, downloadWithToast } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Trash, FilePdf, FloppyDisk } from "@phosphor-icons/react";

let _k = 0;
const nextKey = () => `dl-${Date.now().toString(36)}-${(++_k).toString(36)}`;
const blankLine = () => ({ _key: nextKey(), model: "", description: "", qty: 1, unit_price: 0 });

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DaikinModelCell({ value, catalog, onText, onPick }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const ddRef = useRef(null);
  const q = (value || "").trim().toLowerCase();
  const results = useMemo(() => {
    if (!catalog?.length) return [];
    const list = q ? catalog.filter((it) => `${it.model} ${it.description} ${it.category}`.toLowerCase().includes(q)) : catalog;
    return list.slice(0, 40);
  }, [catalog, q]);

  const place = () => {
    const el = inputRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 420) });
    }
  };
  useEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    const onDoc = (e) => {
      if (inputRef.current?.contains(e.target) || ddRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]); // eslint-disable-line

  return (
    <div className="relative">
      <Input ref={inputRef} value={value} onChange={(e) => { onText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        placeholder="Search Daikin model…" autoComplete="off" className="h-7 text-[11.5px] font-mono" data-testid="daikin-line-model" />
      {open && results.length > 0 && rect && createPortal(
        <div ref={ddRef} style={{ position: "fixed", left: rect.left, top: rect.top, width: rect.width, zIndex: 9999 }}
          className="max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-xl" data-testid="daikin-model-dropdown">
          <div className="px-3 py-1 text-[9px] font-semibold uppercase tracking-widest text-slate-400 bg-slate-50 sticky top-0">
            Daikin catalog · {results.length}{catalog.length > results.length ? `/${catalog.length}` : ""}
          </div>
          {results.map((it) => (
            <button type="button" key={it.id || it.model} onMouseDown={(e) => { e.preventDefault(); onPick(it); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-50 last:border-0 flex items-start justify-between gap-3"
              data-testid={`daikin-model-opt-${it.model}`}>
              <div className="min-w-0">
                <div className="font-mono text-[11.5px] font-semibold text-slate-900">{it.model}</div>
                <div className="text-[10px] text-slate-500 truncate">{it.description || it.category}</div>
              </div>
              <div className="text-[10.5px] font-mono-num text-[#003a8c] whitespace-nowrap">{fmtMoney(it.daikin_list_price || 0)}</div>
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}

export default function DaikinPoEditorSheet({ poId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    ofn_no: "", date: new Date().toISOString().slice(0, 10), ordering_location: "DEL",
    project_sheet_no: "", project_id: "", project_name: "",
    consignee_name: "", consignee_address: "", consignee_contact: "",
    delivery_address: "", dealer_contact: "", mode_of_transport: "", delivery_period: "",
    freight: "Prepaid", payment_terms: "100% advance", special_remarks: "",
    cash_discount_pct: 0, octroi: 0, gst_pct: 18,
  });
  const [items, setItems] = useState([blankLine()]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, catRes, projRes] = await Promise.all([
        api.get("/daikin-pos/settings"),
        api.get("/price-list", { params: { active_only: true, limit: 1000 } }),
        api.get("/projects"),
      ]);
      setCatalog(catRes.data || []);
      setProjects(projRes.data || []);
      if (poId) {
        const { data } = await api.get(`/daikin-pos/${poId}`);
        setForm((f) => ({ ...f, ...data }));
        const its = (data.items || []).map((it) => ({ ...it, _key: nextKey() }));
        setItems(its.length ? its : [blankLine()]);
      } else {
        const { data: nn } = await api.get("/daikin-pos/next-no", { params: { date: form.date } });
        const s = settingsRes.data || {};
        setForm((f) => ({
          ...f, ofn_no: nn.ofn_no, project_sheet_no: nn.ofn_no,
          ordering_location: s.ordering_location || "DEL",
          freight: s.default_freight || "Prepaid",
          payment_terms: s.default_payment_terms || "100% advance",
          gst_pct: s.default_gst_pct ?? 18,
          cash_discount_pct: s.default_cash_discount_pct ?? 0,
        }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [poId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const setLine = (key, patch) => setItems((arr) => arr.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  const addLine = () => setItems((arr) => [...arr, blankLine()]);
  const delLine = (key) => setItems((arr) => (arr.length > 1 ? arr.filter((l) => l._key !== key) : arr));

  const pickProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    if (!p) { setForm((f) => ({ ...f, project_id: "", project_name: "" })); return; }
    const addr = [p.client_address, p.site, p.location].filter(Boolean).join(", ");
    const deliv = [p.site, p.location].filter(Boolean).join(", ");
    setForm((f) => ({
      ...f, project_id: p.id, project_name: p.name,
      consignee_name: p.client_name || f.consignee_name,
      consignee_address: addr || f.consignee_address,
      delivery_address: deliv || f.delivery_address,
    }));
    toast.success(`Consignee filled from ${p.name}`);
  };

  const totals = useMemo(() => {
    const total = items.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
    const totalR = Math.round(total);
    const cdp = Number(form.cash_discount_pct) || 0;
    const discount = cdp > 0 ? Math.round(totalR * cdp / 100) : Math.round(Number(form.cash_discount_amount) || 0);
    const octroi = Math.round(Number(form.octroi) || 0);
    const net = totalR - discount;
    const gst = Math.round(net * (Number(form.gst_pct) || 0) / 100);
    const grand = net + octroi + gst;
    return { totalR, discount, octroi, net, gst, grand };
  }, [items, form.cash_discount_pct, form.cash_discount_amount, form.octroi, form.gst_pct]);

  const payload = () => ({
    ...form,
    cash_discount_pct: Number(form.cash_discount_pct) || 0,
    octroi: Number(form.octroi) || 0,
    gst_pct: Number(form.gst_pct) || 0,
    items: items
      .filter((l) => l.model || l.description || Number(l.qty) || Number(l.unit_price))
      .map((l) => ({ model: l.model, description: l.description, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })),
  });

  const save = async (thenPdf = false) => {
    if (!items.some((l) => l.model || l.description)) { toast.error("Add at least one Daikin model"); return; }
    setBusy(true);
    try {
      const res = poId ? await api.put(`/daikin-pos/${poId}`, payload()) : await api.post("/daikin-pos", payload());
      const id = res.data.id;
      toast.success(`Daikin OFN ${res.data.ofn_no} saved`);
      if (thenPdf) await downloadWithToast(`/daikin-pos/${id}/pdf`, `OFN_${res.data.ofn_no}.pdf`);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto" data-testid="daikin-editor-sheet">
        <SheetHeader>
          <SheetTitle className="text-[16px] flex items-center gap-2">
            <span className="text-[#003a8c] font-extrabold tracking-tight">DAIKIN</span>
            <span>Dealer Purchase Order (OFN) {form.ofn_no ? `· ${form.ofn_no}` : ""}</span>
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="mt-5 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="OFN / Approval No."><Input value={form.ofn_no} onChange={(e) => setForm({ ...form, ofn_no: e.target.value })} disabled={!!poId} className="h-8 text-[12px] font-mono" data-testid="daikin-ofn-no" /></Field>
              <Field label="Project Sheet No."><Input value={form.project_sheet_no} onChange={(e) => setForm({ ...form, project_sheet_no: e.target.value })} placeholder="e.g. PS-1024" className="h-8 text-[12px]" data-testid="daikin-project-sheet-no" /></Field>
              <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-date" /></Field>
              <Field label="Ordering Location"><Input value={form.ordering_location} onChange={(e) => setForm({ ...form, ordering_location: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-ordering-location" /></Field>
              <Field label="Freight"><Input value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} placeholder="Prepaid / To-Pay" className="h-8 text-[12px]" data-testid="daikin-freight" /></Field>
            </div>

            <div className="rounded-md border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#003a8c]">Consignee (Ship To)</div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400">From project</span>
                  <select value={form.project_id || ""} onChange={(e) => pickProject(e.target.value)}
                    className="h-7 text-[11.5px] border border-slate-200 rounded-md px-2 bg-white max-w-[220px]" data-testid="daikin-project-select">
                    <option value="">— none —</option>
                    {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}{p.client_name ? ` · ${p.client_name}` : ""}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Consignee Name"><Input value={form.consignee_name} onChange={(e) => setForm({ ...form, consignee_name: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-consignee-name" /></Field>
                <Field label="Contact Person & Telephone"><Input value={form.consignee_contact} onChange={(e) => setForm({ ...form, consignee_contact: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-consignee-contact" /></Field>
                <Field label="Consignee Address" className="md:col-span-2"><Input value={form.consignee_address} onChange={(e) => setForm({ ...form, consignee_address: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-consignee-address" /></Field>
                <Field label="Delivery Address (floor / building)" className="md:col-span-2"><Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-delivery-address" /></Field>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Dealer Contact (name & phone)"><Input value={form.dealer_contact} onChange={(e) => setForm({ ...form, dealer_contact: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-dealer-contact" /></Field>
              <Field label="Mode of Transport"><Input value={form.mode_of_transport} onChange={(e) => setForm({ ...form, mode_of_transport: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-transport" /></Field>
              <Field label="Delivery Period"><Input value={form.delivery_period} onChange={(e) => setForm({ ...form, delivery_period: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-delivery-period" /></Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#003a8c]">Product / Model details</div>
                <Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#003a8c]" onClick={addLine} data-testid="daikin-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8 text-[10px]">#</TableHead>
                    <TableHead className="text-[10px]">Model</TableHead>
                    <TableHead className="text-[10px]">Description</TableHead>
                    <TableHead className="w-16 text-[10px]">Qty</TableHead>
                    <TableHead className="w-28 text-[10px] text-right">Unit Price</TableHead>
                    <TableHead className="w-28 text-[10px] text-right">Total</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((l, idx) => (
                    <TableRow key={l._key}>
                      <TableCell className="text-[11px] text-slate-400">{idx + 1}</TableCell>
                      <TableCell className="w-44">
                        <DaikinModelCell value={l.model} catalog={catalog}
                          onText={(v) => setLine(l._key, { model: v })}
                          onPick={(it) => setLine(l._key, { model: it.model, description: it.description || l.description, unit_price: it.daikin_list_price ?? l.unit_price })} />
                      </TableCell>
                      <TableCell><Input value={l.description} onChange={(e) => setLine(l._key, { description: e.target.value })} className="h-7 text-[11.5px]" data-testid="daikin-line-desc" /></TableCell>
                      <TableCell><Input type="number" value={l.qty} onChange={(e) => setLine(l._key, { qty: e.target.value })} className="h-7 text-[11.5px] text-center" data-testid="daikin-line-qty" /></TableCell>
                      <TableCell><Input type="number" value={l.unit_price} onChange={(e) => setLine(l._key, { unit_price: e.target.value })} className="h-7 text-[11.5px] text-right" data-testid="daikin-line-price" /></TableCell>
                      <TableCell className="text-right text-[11.5px] font-mono-num">{fmtMoney((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</TableCell>
                      <TableCell><button type="button" onClick={() => delLine(l._key)} className="text-slate-300 hover:text-red-500" data-testid="daikin-line-del"><Trash size={13} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Cash Discount %"><Input type="number" value={form.cash_discount_pct} onChange={(e) => setForm({ ...form, cash_discount_pct: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-cd-pct" /></Field>
                  <Field label="Octroi"><Input type="number" value={form.octroi} onChange={(e) => setForm({ ...form, octroi: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-octroi" /></Field>
                  <Field label="GST %"><Input type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-gst-pct" /></Field>
                </div>
                <Field label="Payment Terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="h-8 text-[12px]" data-testid="daikin-payment-terms" /></Field>
                <Field label="Special Remarks"><Textarea rows={2} value={form.special_remarks} onChange={(e) => setForm({ ...form, special_remarks: e.target.value })} className="text-[12px]" data-testid="daikin-remarks" /></Field>
              </div>
              <div className="rounded-md border border-slate-200 p-3 text-[12px] space-y-1.5" data-testid="daikin-totals">
                <Row k="Total without Tax" v={fmtMoney(totals.totalR)} />
                <Row k="Less: Cash Discount" v={`- ${fmtMoney(totals.discount)}`} />
                <Row k="Net Sales Value" v={fmtMoney(totals.net)} />
                <Row k="Add: Octroi" v={fmtMoney(totals.octroi)} />
                <Row k={`Add: GST @ ${form.gst_pct || 0}%`} v={fmtMoney(totals.gst)} />
                <div className="border-t border-slate-200 pt-1.5 mt-1 flex items-center justify-between font-bold text-[13px] text-[#003a8c]">
                  <span>Net Sales Value</span><span data-testid="daikin-grand-total">{fmtMoney(totals.grand)}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pb-6">
              <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-[12px]">Cancel</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={busy} className="h-9 text-[12px]" data-testid="daikin-save"><FloppyDisk size={14} className="mr-1.5" /> Save</Button>
              <Button onClick={() => save(true)} disabled={busy} className="h-9 text-[12px] bg-[#003a8c] hover:bg-[#002a66] text-white" data-testid="daikin-save-pdf"><FilePdf size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save & PDF"}</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ k, v }) {
  return <div className="flex items-center justify-between text-slate-600"><span>{k}</span><span className="font-mono-num">{v}</span></div>;
}
